<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="3">
                <v-checkbox v-model="fields.layout.centerY" label="Center Y" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="3">
                <label>Gap Item (px)</label>
                <v-text-field v-model.number="fields.layout.gapItem" type="number" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="3">
                <label>Content Position</label>
                <v-select
                  v-model.number="fields.layout.contentOrder"
                  :items="[
                    { text: 'Left', value: 1 },
                    { text: 'Right', value: 2 },
                  ]"
                  hide-details
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12" md="3">
                <ColorPicker
                  :value="fields.layout.backgroundContent"
                  name="background-content"
                  label="Content Background"
                  default-color="#F2F7F9"
                  @input="value => (fields.layout.backgroundContent = value)"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Title & Description</label>
          <div class="group-item">
            <v-checkbox v-model="fields.titleBlock.show" label="Show title block" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.titleBlock.show"
              :editor="editor"
              v-model="fields.titleBlock.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Icon With Text Items</label>
            <v-btn small color="primary" @click="handleAddIconItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Item</span>
            </v-btn>
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.iconItems" :key="`icon-item-${index}`">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <span>Item {{ index + 1 }}</span>
                  <v-btn icon :disabled="fields.iconItems.length <= 1" @click.stop="handleRemoveIconItem(index)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="mb-3">
                  <label>Icon</label>
                  <UploadImage
                    :image="{ imageSrc: item.icon }"
                    :height="`100`"
                    :typeUpload="'image'"
                    @callback="obj => (item.icon = obj.imageSrc)"
                    @callbackDelete="item.icon = ''"
                  />
                </div>
                <div>
                  <label>Content</label>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="mb-5">
          <label class="label-input">Media Image</label>
          <div class="group-item">
            <UploadImage
              :image="{ imageSrc: fields.media.image }"
              :height="`300`"
              :typeUpload="'image'"
              @callback="obj => (fields.media.image = obj.imageSrc)"
              @callbackDelete="fields.media.image = ''"
            />
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';

const defaultIcon = 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/23/image-6a3a4d3cf9049a2d8658864f.png';

const buildIconItemDefault = () => ({
  icon: defaultIcon,
  content:
    '<h3>Total Foot Relief</h3><p>Aching feet slowing you down? <br>Our expert-designed cushioning for cloud-like comfort that lasts all day will help!</p>',
});

const getDefaultFields = () => ({
  title: 'Image With Text Conven Feet 03 Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  layout: {
    centerY: true,
    gapItem: 60,
    contentOrder: 1,
    backgroundContent: '#F2F7F9',
  },
  titleBlock: {
    show: true,
    content:
      '<h2>Proven To Increase Comfort & Mobility</h2><p>Stop letting foot pain dictate your day. Our orthopedic design targets the root cause of discomfort, giving you the freedom to move with zero pain.</p>',
  },
  iconItems: [buildIconItemDefault(), buildIconItemDefault(), buildIconItemDefault()],
  media: {
    image: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/23/image-6a3a4dddb375ec169af0d930.png',
  },
});

export default {
  name: 'ImageWithTextConvenFeet03SectionEditor',
  components: { UploadImage, SectionLayout, ColorPicker },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      layout: { ...def.layout, ...(extract.layout || {}) },
      titleBlock: { ...def.titleBlock, ...(extract.titleBlock || {}) },
      iconItems: Array.isArray(extract.iconItems) && extract.iconItems.length ? extract.iconItems : def.iconItems,
      media: { ...def.media, ...(extract.media || {}) },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionImageWithTextConvenFeet03(
        this.sectionEditor.section.html,
        this.fields,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddIconItem() {
      this.fields.iconItems.push(cloneDeep(buildIconItemDefault()));
    },
    handleRemoveIconItem(index) {
      if (this.fields.iconItems.length <= 1) return;
      this.fields.iconItems.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 140px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
